import { useState, useEffect } from 'react';
import { Calendar, Clock, FileText, AlertCircle } from '../../icons/wp-icons';
import { createAbwesenheit, updateAbwesenheit } from '../../api/abwesenheitApi';
import { getAllBenutzer } from '../../api/benutzerApi';
import { useAuth } from '../../context/AuthContext';

const TYPEN = [
  { value: 'URLAUB', label: 'Urlaub', color: 'bg-green-100 text-green-700' },
  { value: 'KRANKHEIT', label: 'Krankheit', color: 'bg-red-100 text-red-700' },
  { value: 'FORTBILDUNG', label: 'Fortbildung', color: 'bg-blue-100 text-blue-700' },
  { value: 'HOMEOFFICE', label: 'Homeoffice', color: 'bg-orange-100 text-orange-700' },
  { value: 'SONSTIGES', label: 'Sonstiges', color: 'bg-gray-100 text-gray-700' }
];

function AbwesenheitFormular({ abwesenheit, onSuccess, onCancel, benutzerId: propBenutzerId }) {
  const { user } = useAuth();
  const isAdmin = user?.rolle === 'admin';
  const isEditing = !!abwesenheit;
  const isForOther = propBenutzerId && propBenutzerId !== user?.id;

  const [formData, setFormData] = useState({
    typ: 'URLAUB',
    startDatum: '',
    endDatum: '',
    ganztags: true,
    startzeit: '09:00',
    endzeit: '17:00',
    grund: '',
    notiz: '',
    benutzerId: ''
  });

  const [benutzer, setBenutzer] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Lade Benutzer für Admin
  useEffect(() => {
    if (isAdmin) {
      loadBenutzer();
    }
  }, [isAdmin]);

  // Lade Abwesenheit-Daten zum Bearbeiten
  useEffect(() => {
    if (abwesenheit) {
      setFormData({
        typ: abwesenheit.typ,
        startDatum: abwesenheit.startDatum.split('T')[0],
        endDatum: abwesenheit.endDatum.split('T')[0],
        ganztags: abwesenheit.ganztags,
        startzeit: abwesenheit.startzeit || '09:00',
        endzeit: abwesenheit.endzeit || '17:00',
        grund: abwesenheit.grund || '',
        notiz: abwesenheit.notiz || '',
        benutzerId: abwesenheit.benutzerId || ''
      });
    } else {
      // Standard: Heutiges Datum
      const today = new Date().toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        startDatum: today,
        endDatum: today
      }));
    }
  }, [abwesenheit]);

  const loadBenutzer = async () => {
    try {
      const data = await getAllBenutzer();
      setBenutzer(data.filter(b => b.aktiv));
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validierung
    if (!formData.typ || !formData.startDatum || !formData.endDatum) {
      setError('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    if (new Date(formData.startDatum) > new Date(formData.endDatum)) {
      setError('Das Startdatum muss vor dem Enddatum liegen');
      return;
    }

    try {
      setLoading(true);

      const data = {
        typ: formData.typ,
        startDatum: formData.startDatum,
        endDatum: formData.endDatum,
        ganztags: formData.ganztags,
        startzeit: formData.ganztags ? null : formData.startzeit,
        endzeit: formData.ganztags ? null : formData.endzeit,
        grund: formData.grund || null,
        notiz: formData.notiz || null
      };

      // Admin kann für andere eintragen
      if (isAdmin && (formData.benutzerId || propBenutzerId)) {
        data.benutzerId = formData.benutzerId || propBenutzerId;
      }

      if (isEditing) {
        await updateAbwesenheit(abwesenheit.id, data);
      } else {
        await createAbwesenheit(data);
      }

      onSuccess();
    } catch (err) {
      console.error('Fehler:', err);
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  // Zeit-Optionen generieren
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Fehler */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Info für Krankheit */}
      {formData.typ === 'KRANKHEIT' && !isEditing && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          Krankheitsmeldungen werden automatisch genehmigt.
        </div>
      )}

      {/* Admin: Mitarbeiter auswählen (nur wenn nicht bereits festgelegt) */}
      {isAdmin && !isEditing && !propBenutzerId && (
        <div>
          <label className="block text-sm font-medium mb-2">
            Mitarbeiter
          </label>
          <select
            name="benutzerId"
            value={formData.benutzerId}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Für mich selbst</option>
            {benutzer.filter(b => b.id !== user?.id).map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Typ */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Typ <span className="text-danger">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TYPEN.map(typ => (
            <button
              key={typ.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, typ: typ.value }))}
              className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition ${
                formData.typ === typ.value
                  ? `${typ.color} border-current`
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              {typ.label}
            </button>
          ))}
        </div>
      </div>

      {/* Datum */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Von <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            name="startDatum"
            value={formData.startDatum}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Bis <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            name="endDatum"
            value={formData.endDatum}
            onChange={handleChange}
            min={formData.startDatum}
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
        </div>
      </div>

      {/* Ganztags Toggle */}
      <div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            name="ganztags"
            checked={formData.ganztags}
            onChange={handleChange}
            className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
          />
          <span className="font-medium">Ganztägig</span>
        </label>
      </div>

      {/* Uhrzeiten (nur wenn nicht ganztags) */}
      {!formData.ganztags && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Von Uhrzeit
            </label>
            <select
              name="startzeit"
              value={formData.startzeit}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {timeOptions.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Bis Uhrzeit
            </label>
            <select
              name="endzeit"
              value={formData.endzeit}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {timeOptions.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Grund */}
      <div>
        <label className="block text-sm font-medium mb-2">
          <FileText className="w-4 h-4 inline mr-1" />
          Grund (optional)
        </label>
        <textarea
          name="grund"
          value={formData.grund}
          onChange={handleChange}
          rows="2"
          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="z.B. Familienurlaub, Arztbesuch..."
        />
      </div>

      {/* Notiz (intern) */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Interne Notiz (optional)
        </label>
        <textarea
          name="notiz"
          value={formData.notiz}
          onChange={handleChange}
          rows="2"
          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Interne Anmerkungen..."
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50"
        >
          {loading ? 'Speichern...' : isEditing ? 'Aktualisieren' : 'Beantragen'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

export default AbwesenheitFormular;
